"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Public landing route (T10, 2026-08 visual refresh). main.html's
 * landing rebuilt as typed React components. Must never load personal
 * context: it only reads i18n copy and the (unauthenticated) auth
 * panel. Embedded auth per ADR-014; soft-launch banner per ADR-015.
 *
 * Section order follows docs/product/ui-ux-spec.md's "Public landing
 * page" required order (hero, honest value strip, four-step journey,
 * product walkthrough, savings, trust, pricing, employer section, FAQ +
 * final CTA), with the demo/save-the-day/contact sections main.html had
 * restored between them (the previous build had cut the employer and
 * contact sections — both are back per the 2026-08 landing refresh).
 */

import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { AuthPanel } from "@/components/auth/AuthPanel"
import type { AuthMode } from "@/components/auth/authMode"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import { CONTACT_PATH } from "@/lib/legalRoutes"
import { LandingHeader } from "@/components/landing/LandingHeader"
import { HeroV2 } from "@/components/landing/HeroV2"
import { IngredientMarquee } from "@/components/landing/IngredientMarquee"
import { ValueStrip } from "@/components/landing/ValueStrip"
import { HowItWorks } from "@/components/landing/HowItWorks"
import { ProductWalkthrough } from "@/components/landing/ProductWalkthrough"
import { SavingsSection } from "@/components/landing/SavingsSection"
import { DemoSection } from "@/components/landing/DemoSection"
import { SaveTheDaySection } from "@/components/landing/SaveTheDaySection"
import { TrustSection } from "@/components/landing/TrustSection"
import { GuidesStrip } from "@/components/landing/GuidesStrip"
import { PricingSection } from "@/components/landing/PricingSection"
import { EmployerSection } from "@/components/landing/EmployerSection"
import { FaqSection } from "@/components/landing/FaqSection"
import { ContactSection, type ContactTopic } from "@/components/landing/ContactSection"
import { FinalCta } from "@/components/landing/FinalCta"
import { LandingFooter } from "@/components/landing/LandingFooter"
import { trackFunnelEvent } from "@/lib/analytics"

interface LocationState {
  authRequiredFrom?: string
}

export default function LandingPage() {
  const { t } = useI18n()
  const { status } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [authOpen, setAuthOpen] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>("signIn")
  const [contactTopic, setContactTopic] = useState<ContactTopic | null>(null)

  const authRequiredFrom = (location.state as LocationState | null)?.authRequiredFrom
  // Where to return after sign-in, captured ONCE from router state. The
  // history entry's state is then scrubbed immediately: router state lives in
  // history.state, which SURVIVES page refreshes — without the scrub, any
  // reload of an entry created by an auth bounce ("/app/x" → "/" with
  // authRequiredFrom) re-opened the sign-in dialog over the landing page
  // (live-reported 2026-08-27: "sometimes shows the log in interface").
  const [pendingAuthReturn, setPendingAuthReturn] = useState<string | null>(null)

  useEffect(() => {
    if (authRequiredFrom) {
      setPendingAuthReturn(authRequiredFrom)
      setAuthMode("signIn")
      setAuthOpen(true)
      navigate(location.pathname + location.search + location.hash, { replace: true, state: null })
    }
  }, [authRequiredFrom, navigate, location.pathname, location.search, location.hash])

  const onContactPath = location.pathname === CONTACT_PATH

  useEffect(() => {
    // /kontakt stays put for a signed-in visitor: the contact form must be reachable by everyone.
    if (status === "signedIn" && !(onContactPath && !pendingAuthReturn)) {
      navigate(pendingAuthReturn ?? "/app/today", { replace: true })
    }
  }, [status, navigate, pendingAuthReturn, onContactPath])

  // /kontakt (the Impressum's link) and /#contact open scrolled to the form; the
  // page renders after JS loads, so the browser's own anchor jump can miss it.
  useEffect(() => {
    if (!onContactPath && location.hash !== "#contact") return
    const frame = requestAnimationFrame(() => {
      document.getElementById("contact")?.scrollIntoView({ block: "start" })
    })
    return () => cancelAnimationFrame(frame)
  }, [onContactPath, location.hash])

  // G04: the top of the acquisition funnel. Fires on every mount, including
  // the brief flash for an already-signed-in visitor mid-redirect — a plain
  // "page was shown" signal, not an attempt to out-guess the redirect.
  useEffect(() => {
    trackFunnelEvent("landing_page_viewed")
  }, [])

  function openAuth(mode: AuthMode) {
    setAuthMode(mode)
    setAuthOpen(true)
  }

  return (
    <div className="landing-v2 min-h-screen bg-cream text-ink">
      <a
        href="#main"
        className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-50 focus-visible:rounded-bb-md focus-visible:bg-paper focus-visible:px-4 focus-visible:py-2 focus-visible:text-ink focus-visible:shadow-[var(--bb-shadow)]"
      >
        {t("nav.skipToContent")}
      </a>

      <LandingHeader onOpenAuth={openAuth} />

      <main id="main">
        <HeroV2 onOpenAuth={openAuth} />
        <IngredientMarquee />
        <ValueStrip />
        <HowItWorks />
        <ProductWalkthrough />
        <SavingsSection />
        <DemoSection />
        <SaveTheDaySection />
        <TrustSection />
        <GuidesStrip />
        <PricingSection onOpenAuth={openAuth} />
        <EmployerSection onRequestPilot={() => setContactTopic("employer")} />
        <FaqSection />
        <ContactSection prefilledTopic={contactTopic} />
        <FinalCta onOpenAuth={openAuth} />
      </main>

      <LandingFooter />

      <AuthPanel
        open={authOpen}
        initialMode={authMode}
        onOpenChange={setAuthOpen}
        onAuthenticated={() => setAuthOpen(false)}
      />

    </div>
  )
}
